import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { absencesAPI, employeesAPI, companyAPI } from '../lib/api';
import { formatLastFirst } from '../lib/nameFormat';
import { Plus, Calendar, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { ABSENCE_TYPES, emptyAbsenceForm } from './absences/constants';
import { AbsenceSummaryCards } from './absences/AbsenceSummaryCards';
import { AbsencesTable } from './absences/AbsencesTable';
import { AbsenceDialog } from './absences/AbsenceDialog';
import { WeeklyPayrollReportCard } from './absences/WeeklyPayrollReportCard';

export default function Absences() {
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState(emptyAbsenceForm);
  const [koTagEnabled, setKoTagEnabled] = useState(true);

  useEffect(() => {
    companyAPI.get()
      .then((res) => setKoTagEnabled(res.data?.ko_tag_enabled !== false))
      .catch(() => { /* default true */ });
  }, []);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [absRes, empRes, summaryRes] = await Promise.all([
        absencesAPI.getAll({ start_date: `${selectedYear}-01-01`, end_date: `${selectedYear}-12-31` }),
        employeesAPI.getAll({ is_active: true }),
        absencesAPI.getSummary(selectedYear),
      ]);
      setAbsences(absRes.data);
      setEmployees(empRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => { loadData(); }, [loadData]);

  const openDialog = (absence = null) => {
    if (absence) {
      setEditingAbsence(absence);
      setFormData({
        employee_id: absence.employee_id,
        start_date: absence.start_date,
        end_date: absence.end_date,
        absence_type: absence.absence_type,
        notes: absence.notes || '',
        doctor_note: absence.doctor_note || false,
        doctor_note_date: absence.doctor_note_date || '',
        employer_confirmation: absence.employer_confirmation || false,
        child_sick_certificate: absence.child_sick_certificate || false,
        certificate_type: absence.certificate_type || '',
        attachments: absence.attachments || [],
      });
    } else {
      setEditingAbsence(null);
      setFormData(emptyAbsenceForm);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.employee_id || !formData.start_date || !formData.end_date) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    if (formData.start_date > formData.end_date) {
      toast.error('Enddatum muss nach Startdatum liegen');
      return;
    }
    if (formData.absence_type === 'K' && !formData.certificate_type) {
      toast.error('Bitte Bescheinigung auswählen (AUB, eAUB oder Noch offen)');
      return;
    }
    setSaving(true);
    try {
      // Bei K: certificate_type setzt automatisch doctor_note (true/false)
      const payload = {
        ...formData,
        certificate_type: formData.absence_type === 'K' ? (formData.certificate_type || null) : null,
        doctor_note: formData.absence_type === 'K'
          ? (formData.certificate_type && formData.certificate_type !== 'none')
          : formData.doctor_note,
      };
      if (editingAbsence) {
        await absencesAPI.update(editingAbsence.id, payload);
        toast.success('Abwesenheit aktualisiert');
      } else {
        await absencesAPI.create(payload);
        toast.success('Abwesenheit erfasst');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diese Abwesenheit wirklich löschen?')) return;
    try {
      await absencesAPI.delete(id);
      toast.success('Abwesenheit gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const filteredAbsences = absences.filter((a) => {
    if (filterEmployee !== 'all' && a.employee_id !== filterEmployee) return false;
    if (filterType !== 'all' && a.absence_type !== filterType) return false;
    return true;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={`skel-${i}`} className="h-28 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="absences-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Abwesenheiten</h1>
            <p className="text-muted-foreground mt-1">
              Krankheit, Kind krank, Elternzeit & Unbezahlter Urlaub erfassen
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[120px]">
                <Calendar className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button className="rounded-full" onClick={() => openDialog()} data-testid="new-absence-btn">
              <Plus className="mr-2 h-4 w-4" />
              Abwesenheit erfassen
            </Button>
          </div>
        </div>

        <AbsenceSummaryCards summary={summary} year={selectedYear} />

        <WeeklyPayrollReportCard />

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Filter:</span>
          </div>
          <Select value={filterEmployee} onValueChange={setFilterEmployee}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Alle Mitarbeiter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Mitarbeiter</SelectItem>
              {employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {formatLastFirst(emp)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Alle Typen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Typen</SelectItem>
              {ABSENCE_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.short} - {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <AbsencesTable
          rows={filteredAbsences}
          year={selectedYear}
          onEdit={openDialog}
          onDelete={handleDelete}
          onCreate={() => openDialog()}
        />

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm">
          {ABSENCE_TYPES.map((type) => (
            <div key={type.value} className="flex items-center gap-2">
              <Badge className={type.color}>
                <type.icon className="h-3 w-3 mr-1" />
                {type.short}
              </Badge>
              <span className="text-muted-foreground">{type.label}</span>
            </div>
          ))}
        </div>

        <AbsenceDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          formData={formData}
          setFormData={setFormData}
          employees={employees}
          isEdit={!!editingAbsence}
          saving={saving}
          onSubmit={handleSubmit}
          koTagEnabled={koTagEnabled}
        />
      </div>
    </DashboardLayout>
  );
}
